import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  Search,
  Users,
  Clock,
  MapPin,
  Filter,
  X,
  ShieldCheck,
  Ruler,
  Calendar,
  Trophy,
  Mail,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import { useSeason } from "../context/SeasonContext";
import { useAuth } from "../context/AuthContext";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";

const POSITIONS = [
  { key: "PG", label: "Point Guard" },
  { key: "SG", label: "Shooting Guard" },
  { key: "SF", label: "Small Forward" },
  { key: "PF", label: "Power Forward" },
  { key: "C", label: "Center" },
];

//const AGE_RANGES = ["17-20", "21-25", "26-30", "31-35", "36-40", "41+"];

const formatHeight = (totalInches) => {
  if (!totalInches) return null;
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return `${feet}'${inches}"`;
};

const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-500/10">
      <Icon size={20} className="text-orange-500" />
    </div>
    <h2 className="text-2xl font-semibold tracking-tight dark:text-white">
      {title}
    </h2>
  </div>
);

const fetchSeasonHistory = async (profileId) => {
  const { data: linked } = await supabase
    .from("players")
    .select(
      "id, season_id, seasons(name), teams(name, primary_color, secondary_color, gradient)",
    )
    .eq("profile_id", profileId)
    .order("season_id", { ascending: false });

  if (!linked?.length) return [];

  const playerIds = linked.map((p) => p.id);
  const { data: statRows } = await supabase
    .from("game_stats")
    .select(
      "player_id, two_pm, three_pm, ft_made, points, rebounds, assists, personal_fouls, technical_fouls, games!inner(counts_in_standings)",
    )
    .in("player_id", playerIds)
    .eq("games.counts_in_standings", true);

  const byPlayer = {};
  (statRows || []).forEach((row) => {
    // ── DNP Filter: Skip games where all stats are zero ──
    const isDNP =
      row.points === 0 &&
      row.rebounds === 0 &&
      row.assists === 0 &&
      row.three_pm === 0 &&
      row.two_pm === 0 &&
      row.ft_made === 0 &&
      (row.personal_fouls ?? 0) === 0 &&
      (row.technical_fouls ?? 0) === 0;
    if (isDNP) return;

    if (!byPlayer[row.player_id]) byPlayer[row.player_id] = { gp: 0, pts: 0 };
    byPlayer[row.player_id].gp += 1;
    byPlayer[row.player_id].pts +=
      row.two_pm * 2 + row.three_pm * 3 + row.ft_made;
  });

  return linked.map((p) => {
    const s = byPlayer[p.id];
    return {
      seasonName: p.seasons?.name,
      team: p.teams,
      avgPts: s ? (s.pts / s.gp).toFixed(1) : null,
    };
  });
};

const SeasonHistoryTrigger = ({ agent }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleEnter = () => {
    if (history !== null || loading || !agent.profile_id) return;
    setLoading(true);
    fetchSeasonHistory(agent.profile_id).then((rows) => {
      setHistory(rows);
      setLoading(false);
    });
  };

  if (agent.seasons_played <= 0) {
    return (
      <span className="flex items-center gap-1.5">
        <Trophy size={13} /> New to the league
      </span>
    );
  }

  return (
    <span
      className="relative group/history flex items-center gap-1.5 cursor-default"
      onMouseEnter={handleEnter}
    >
      <Trophy size={13} />
      {`${agent.seasons_played} season${agent.seasons_played === 1 ? "" : "s"} played`}

      <div className="pointer-events-none absolute left-0 bottom-full mb-2 z-20 w-72 opacity-0 invisible group-hover/history:opacity-100 group-hover/history:visible transition-opacity duration-150">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 space-y-2">
          {loading || history === null ? (
            <p className="text-xs text-gray-400 px-1">Loading history…</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-gray-400 px-1">No recorded seasons.</p>
          ) : (
            history.map((h, i) => {
              const style = h.team?.name ? getTeamStyle(h.team) : null;
              const textColor = style
                ? getTextColorForAccent(style.primaryHex, isDarkModeActive())
                : null;
              return (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 text-xs"
                >
                  <span className="text-gray-500 dark:text-gray-400 shrink-0">
                    {h.seasonName}
                  </span>
                  {h.team?.name ? (
                    <span
                      className="px-2 py-1 rounded-full font-semibold truncate"
                      style={{ ...style.headerStyle, color: textColor }}
                    >
                      {h.team.name}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                  <span className="font-bold text-gray-700 dark:text-gray-200 shrink-0">
                    {h.avgPts != null ? `${h.avgPts} PPG` : "—"}
                  </span>
                </div>
              );
            })
          )}
        </div>
        <div className="w-3 h-3 bg-white dark:bg-gray-900 border-r border-b border-gray-200 dark:border-gray-700 rotate-45 -mt-1.5 ml-4" />
      </div>
    </span>
  );
};

const FreeAgentCard = ({
  agent,
  index,
  canContact,
  contactStatus,
  onRequestContact,
  ageRange,
}) => {
  const height = formatHeight(agent.height_inches);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.4) }}
      className="bg-white dark:bg-gray-900 rounded-3xl p-6 border border-gray-100 dark:border-gray-800 hover:border-orange-200 dark:hover:border-orange-900 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-lg dark:text-white">
            {agent.name}
          </div>
          {agent.former_team_name ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-1">
              <MapPin size={13} /> Previously: {agent.former_team_name}
            </div>
          ) : (
            <div className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
              <ShieldCheck size={13} /> New to the league
            </div>
          )}
        </div>
        {ageRange && (
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center gap-1 shrink-0">
            <Calendar size={11} /> {ageRange}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-4 text-sm text-gray-500 dark:text-gray-400">
        {height && (
          <span className="flex items-center gap-1.5">
            <Ruler size={13} /> {height}
          </span>
        )}
        <SeasonHistoryTrigger agent={agent} />
      </div>

      {agent.positions?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {agent.positions.map((pos) => (
            <span
              key={pos}
              className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
            >
              {pos}
            </span>
          ))}
        </div>
      )}

      {agent.note && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 leading-relaxed">
          "{agent.note}"
        </p>
      )}

      <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
        {!canContact ? (
          <Link
            to="/login"
            className="text-sm text-orange-600 dark:text-orange-400 font-medium hover:underline flex items-center gap-1.5"
          >
            <Mail size={14} /> Log in as a captain to request contact
          </Link>
        ) : contactStatus === "sent" ? (
          <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1.5">
            <CheckCircle2 size={14} /> Request sent
          </div>
        ) : (
          <button
            onClick={() => onRequestContact(agent)}
            disabled={contactStatus === "sending"}
            className="text-sm font-medium px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-orange-100 dark:hover:bg-orange-950/40 hover:text-orange-700 dark:hover:text-orange-400 transition-colors flex items-center gap-1.5 disabled:opacity-60"
          >
            <Mail size={14} />
            {contactStatus === "sending" ? "Sending..." : "Request Contact"}
          </button>
        )}
        {contactStatus === "error" && (
          <p className="text-xs text-red-500 mt-2 flex items-center gap-1.5">
            <AlertTriangle size={12} /> Couldn't send that — try again in a
            moment.
          </p>
        )}
      </div>
    </motion.div>
  );
};

const ClosedPlaceholder = ({ registrationSeason }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/40 border border-orange-100 dark:border-orange-900 rounded-3xl p-12 text-center"
  >
    <div className="flex justify-center mb-5">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white dark:bg-gray-900 shadow-sm">
        <Clock className="text-orange-500" size={26} />
      </div>
    </div>
    <h3 className="text-2xl font-semibold dark:text-white mb-2">
      The Free Agent list isn't open yet!
    </h3>
    <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
      There is currently no active registration period. Once it opens, players
      looking for a new team next season will appear here.
    </p>
  </motion.div>
);

const EmptyState = () => (
  <div className="text-center py-16">
    <Users
      className="mx-auto text-gray-300 dark:text-gray-700 mb-4"
      size={40}
    />
    <p className="text-gray-500 dark:text-gray-400 font-medium">
      No free agents listed right now
    </p>
    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
      Check back soon — players can join the pool anytime before next season
      starts.
    </p>
  </div>
);

const ContactRequestModal = ({
  agent,
  message,
  onMessageChange,
  sending,
  onSend,
  onCancel,
}) => (
  <AnimatePresence>
    {agent && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
          className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
        >
          <div className="p-8">
            <h3 className="text-2xl font-semibold dark:text-white mb-2">
              Contact {agent.name}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-5">
              We'll email them your team name and your contact info. They choose
              whether to respond — you won't see their email or phone directly.
            </p>
            <textarea
              value={message}
              onChange={(e) => onMessageChange(e.target.value)}
              rows={3}
              placeholder="Optional note, e.g. we play Sundays and could use a guard"
              className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
            />
          </div>
          <div className="border-t dark:border-gray-800 flex">
            <button
              onClick={onCancel}
              disabled={sending}
              className="flex-1 py-4 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onSend}
              disabled={sending}
              className="flex-1 py-4 text-sm font-semibold bg-orange-600 hover:bg-orange-700 text-white transition-colors disabled:opacity-70"
            >
              {sending ? "Sending..." : "Send Request"}
            </button>
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

const FreeAgents = () => {
  const { registrationSeason } = useSeason();
  const { isCaptain, isAdmin } = useAuth();
  const canContact = isCaptain || isAdmin;

  const [ageRanges, setAgeRanges] = useState({}); // { [free_agent_id]: age_range } — admin only, populated below

  const [contactModalAgent, setContactModalAgent] = useState(null);
  const [contactMessage, setContactMessage] = useState("");
  const [contactStatus, setContactStatus] = useState({}); // { [agentId]: 'sending' | 'sent' | 'error' }

  const handleSendContact = async () => {
    const agentId = contactModalAgent.id;
    setContactStatus((prev) => ({ ...prev, [agentId]: "sending" }));

    const { error } = await supabase.functions.invoke(
      "request-free-agent-contact",
      {
        body: {
          free_agent_id: agentId,
          message: contactMessage.trim() || null,
        },
      },
    );

    if (error) {
      setContactStatus((prev) => ({ ...prev, [agentId]: "error" }));
    } else {
      setContactStatus((prev) => ({ ...prev, [agentId]: "sent" }));
      setContactModalAgent(null);
      setContactMessage("");
    }
  };

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activePositions, setActivePositions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const isRegistrationActive = registrationSeason;
  const canView = isRegistrationActive || isAdmin;
  const previewingAsAdmin = isAdmin && !isRegistrationActive;

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const fetchAgents = async () => {
      setLoading(true);
      setError(null);
      const { data, error: fetchErr } = await supabase
        .from("free_agent_list")
        .select("*")
        .order("opted_at", { ascending: false });

      if (cancelled) return;
      if (fetchErr) {
        setError("Couldn't load the free agent list. Try refreshing.");
      } else {
        setAgents(data || []);
      }
      setLoading(false);
    };

    fetchAgents();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  // Age is intentionally excluded from the public free_agent_list view.
  // This queries the raw table instead — RLS only allows admins (and each
  // player, for their own row) to read it, so this silently returns nothing
  // for anyone else. Extend the RLS policy to include team_captain later
  // and this will start working for captains automatically.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;

    supabase
      .from("free_agents")
      .select("id, age_range")
      .then(({ data }) => {
        if (cancelled || !data) return;
        const map = {};
        data.forEach((row) => {
          if (row.age_range) map[row.id] = row.age_range;
        });
        setAgeRanges(map);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const togglePosition = (key) => {
    setActivePositions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  const filtered = useMemo(() => {
    return agents.filter((a) => {
      const matchesSearch = a.name.toLowerCase().includes(search.toLowerCase());
      const matchesPositions =
        activePositions.length === 0 ||
        activePositions.some((p) => a.positions?.includes(p));
      return matchesSearch && matchesPositions;
    });
  }, [agents, search, activePositions]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="mb-10">
        <h1 className="text-4xl font-bold tracking-tighter dark:text-white mb-2">
          Free Agents
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Players looking for a team next season
        </p>
      </div>

      {!canView ? (
        <ClosedPlaceholder registrationSeason={registrationSeason} />
      ) : (
        <>
          {previewingAsAdmin && (
            <div className="flex items-center gap-3 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-5 text-amber-700 dark:text-amber-300 mb-8">
              <ShieldCheck size={20} className="shrink-0" />
              <div className="text-sm">
                <span className="font-semibold">Admin preview.</span> This list
                isn't public yet — there is no active registration period.
              </div>
            </div>
          )}
          <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-6 mb-8">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name..."
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                />
              </div>
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-medium border-2 transition-colors ${
                  activePositions.length > 0
                    ? "border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/40"
                    : "border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300"
                }`}
              >
                <Filter size={16} />
                Position
                {activePositions.length > 0 && (
                  <span className="text-xs bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                    {activePositions.length}
                  </span>
                )}
              </button>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="flex flex-wrap gap-2 pt-5 mt-5 border-t border-gray-100 dark:border-gray-800">
                    {POSITIONS.map((pos) => {
                      const active = activePositions.includes(pos.key);
                      return (
                        <button
                          key={pos.key}
                          onClick={() => togglePosition(pos.key)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            active
                              ? "bg-orange-600 text-white"
                              : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                          }`}
                        >
                          {pos.label}
                        </button>
                      );
                    })}
                    {activePositions.length > 0 && (
                      <button
                        onClick={() => setActivePositions([])}
                        className="px-4 py-2 rounded-full text-sm font-medium text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1"
                      >
                        <X size={14} /> Clear
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <SectionHeader
            icon={Users}
            title={loading ? "Loading..." : `${filtered.length} Available`}
          />

          {error && (
            <div className="p-5 border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 rounded-2xl text-red-700 dark:text-red-400 mb-6">
              {error}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-32 bg-gray-100 dark:bg-gray-800 rounded-3xl animate-pulse"
                />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filtered.map((agent, i) => (
                <FreeAgentCard
                  key={agent.id}
                  agent={agent}
                  index={i}
                  canContact={canContact}
                  contactStatus={contactStatus[agent.id]}
                  onRequestContact={setContactModalAgent}
                  ageRange={ageRanges[agent.id]}
                />
              ))}
            </div>
          )}
        </>
      )}

      <ContactRequestModal
        agent={contactModalAgent}
        message={contactMessage}
        onMessageChange={setContactMessage}
        sending={contactStatus[contactModalAgent?.id] === "sending"}
        onSend={handleSendContact}
        onCancel={() => {
          setContactModalAgent(null);
          setContactMessage("");
        }}
      />
    </div>
  );
};

export default FreeAgents;
